import axios, {AxiosError, AxiosPromise, AxiosRequestConfig} from 'axios'

import store, {DialogState} from '@/store/index'

import {envInstance} from '@/lib/env'

import ErrorView from '@/components/Error.vue'
// import ConfirmDialog from '@/views/ConfirmDialog.vue'

const axiosInstance = axios.create({
  baseURL: envInstance.API_URL,
})

const tokenIsAvailable: () => boolean = () => {
  const token = store.state.userToken
  return !(typeof (token) === 'undefined' || token == null || token.trim() === '')
}

const isMainDriver: () => boolean = () => {
  return false
}

const headerLang: () => string = () => {
  return store.state.configuration.appConfig ? store.state.configuration.appConfig.default_language : 'en'
}

// Add a request interceptor
axiosInstance.interceptors.request.use((config: AxiosRequestConfig) => {
  // Do something before request is sent
  if ((config as any).clientOnly === true) {
    if (!isMainDriver()) {
      // Not main driver, abort the request
      const source = axios.CancelToken.source()
      config.cancelToken = source.token
      source.cancel('Not the main driver')
    }
  }
  if (typeof ((config as any).secured) === 'undefined' || (config as any).secured === true) {
    if (!tokenIsAvailable()) {
      // No token availabile, abort the request
      const source = axios.CancelToken.source()
      config.cancelToken = source.token
      source.cancel('No login token is defined')
    } else {
      config.headers.Authorization = 'Bearer ' + store.state.userToken
    }
  }
  config.headers.lang = headerLang()
  return config
}, (error: any) => {
  // Do something with request error
  return Promise.reject(error)
})

// Add a response interceptor
axiosInstance.interceptors.response.use((response: any) => {
  // Do something with response data
  // --> NO ERRORS
  return response
}, (error: AxiosError) => {

  // Do something with response error
  // const messages = error.messages.join(', ')
  // if (response.status == 403) {
  // --> ERROR!
  // console.log('FUCKING ERROR: ', error.response)
  //  alert("403! You will now be logged out");
  //  localStorage.removeItem('backoffice-token')
  // }

  if (axios.isCancel(error)) {
    return Promise.reject(error)
  }

  const code = (e: AxiosError) => {
    if (e.response) {
      const status: number = e.response.status
      const statusText: string | undefined = e.response.statusText
      if (e.response.statusText) {
        return `status ${status}: ${statusText}`
      } else {
        return `status ${status}`
      }
    }
    return ''
  }

  const message = (e: AxiosError) => {
    if (e.response) {
      const messages: [string] = e.response.data.messages
      return messages.join('\n')
    }
    return ''
  }

  if (error.response && error.response.data.result_code === 'exceptions.system.already-has-account-exception') {
    throw error
  }

  if (error.response && error.response.data.result_code === 'exceptions.reservation.zone-exception') {
    return Promise.reject(error)
  }

  if (error.message === 'No login token is defined') {
    return Promise.reject(error)
  }

  if (message(error) === 'auth.invalid') {
    store.dispatch('logout')
    // this cuts off further promises, but...
    location.reload()
    // TODO: figure this out, with multiple requests in queue it repeats the alert for each.
    // is there some way to stop further promises after the first that returns 403?
    // alert("Invalid token, you have been logged out.")
  }

  const errorCode = code(error)
  const errorTitle = error.response ? error.response.data.result : 'error'
  const errorSubtitle = message(error)
  const errorProps: any = {
    code: errorCode,
    title: errorTitle,
    subtitle: errorSubtitle,
  }

  if (!navigator.onLine || error.message === 'Network Error') {
    errorProps.code = ''
    errorProps.title = 'common.you-are-offline'
    errorProps.subtitle = 'common.check-your-connection'
    errorProps.image = null
  }

  store.dispatch('openDialog', new DialogState(ErrorView, errorProps))

  return Promise.reject(error)
})

const noAuth = (extra: {} = {}) => {
  return Object.assign({secured: false}, extra) as AxiosRequestConfig
}

const clientOnly = (extra: {} = {}) => {
  return Object.assign({clientOnly: true}, extra) as AxiosRequestConfig
}

import {
  LoginByPhoneCodeRequest,
  LoginByPhoneRequest,
  LoginRequest,
  ResetPasswordRequest,
  ReservationResponse,
  SearchRequest,
  ModifyRequest,
  VehicleSlot,
  DriverInvite,
  Document,
  PaginatedRequest,
  Driver,
  UserMesh,
  DriverEditRequest,
  AppConfig,
  ProfileImageRequest,
  Timestamps,
  TocResponse,
  TosResponse,
  QuickRegistrationRequest,
  LoginResponse,
  TermsOfServiceResponse,
  ClientTypeResponse,
} from '@/lib//interfaces'
import {DriverLicense, RegistrationRequest} from '@/store/modules/registration'

export default {
  booking: {
    publicSearch: (req: SearchRequest) => {
      return axiosInstance.get('v1/public_search', noAuth({params: req})) as AxiosPromise<VehicleSlot[]>
    },
    publicCategories() {
      return axiosInstance.get('v1/public_categories', noAuth()) as AxiosPromise<any>
    },
  },
  people: {
    isLogged: () => {
      return tokenIsAvailable()
    },
    login: (req: LoginRequest): AxiosPromise<LoginResponse> => {
      return axiosInstance.post('v1/login', req, noAuth()) as AxiosPromise<LoginResponse>
    },
    client: () => {
      return axiosInstance.get('v1/client')
    },
    clientToken: () => {
      return axiosInstance.get('v1/client/token')
    },
    status: (bookingMode: string) => {
      return axiosInstance.get('v1/drivers/account_status/' + bookingMode)
    },
    clientTypes: () => {
      return axiosInstance.get('v1/client/types', noAuth()) as AxiosPromise<ClientTypeResponse>
    },
    licenseTypes: () => {
      return axiosInstance.get('v1/license/types', noAuth())
    },
    tocs: () => {
      return axiosInstance.get('v1/config/service_toc', noAuth()) as AxiosPromise<TermsOfServiceResponse>
    },
    globalNotes: () => {
      return axiosInstance.get('frontoffice/v2/config/global_notes', noAuth()) as AxiosPromise
    },
    register: (req: RegistrationRequest) => {
      return axiosInstance.post('v1/client/register', req, noAuth())
    },
    quickRegister: (req: QuickRegistrationRequest) => {
      return axiosInstance.post('frontoffice/v2/auth/register', req, noAuth())
    },
    forgotPassword(req: ResetPasswordRequest) {
      return axiosInstance.post('v1/driver/password/forgot', req, noAuth())
    },
    drivers: () => {
      return axiosInstance.get('v1/drivers', clientOnly())
    },
    driver: () => {
      return axiosInstance.get('v1/driver')
    },
    requestLoginByPhone(req: LoginByPhoneCodeRequest) {
      return axiosInstance.post('frontoffice/v2/auth/request_login_by_phone', req, noAuth())
    },
    loginByPhone(req: LoginByPhoneRequest) {
      return axiosInstance.post('frontoffice/v2/auth/login_by_phone', req, noAuth())
    },
    reservations(req: PaginatedRequest) {
      return axiosInstance.get(`v1/reservation/list/${req.per_page}/${req.page_number}`)
    },
    addDriverLicense(req: DriverLicense) {
      return axiosInstance.post('v1/driver_license/add', req) as AxiosPromise<Document>
    },
    addProfilePicture(req: ProfileImageRequest) {
      return axiosInstance.post('v1/driver/add_profile_image', req) as AxiosPromise<Driver>
    },
    accountStatus(req: string) {
      return axiosInstance.get(`v1/drivers/account_status/${req}`) as AxiosPromise<Driver>
    },
  },
  app: {
    config() {
      return axiosInstance.get(`v1/config`, noAuth()) as AxiosPromise<AppConfig>
    },
    version() {
      return axiosInstance.get(`v1/config/app_version`, noAuth()) as AxiosPromise
    },
    timestamps() {
      return axiosInstance.get('frontoffice/v2/drivers/timestamps') as AxiosPromise<Timestamps>
    },
  },
  timestamps: {
    now: () => Math.round(new Date().getTime() / 1000),
    verify: (timestamp?: number, threshold?: number) => {
      if (threshold === undefined) {
        threshold = 300
      }
      return timestamp === undefined || (new Date().getTime() / 1000) - timestamp >= threshold
    },
  },
  tocs: {
    getTocs() {
      return axiosInstance.get(`frontoffice/v2/tocs`) as AxiosPromise<TocResponse[]>
    },
    getTos() {
      return axiosInstance.get('frontoffice/v2/public/tos', noAuth()) as AxiosPromise<TosResponse[]>
    },
    getSingleToc(id: string) {
      return axiosInstance.get(`frontoffice/v2/tocs/${id}`) as AxiosPromise<TocResponse>
    },
    signToc(id: string) {
      return axiosInstance.post(`frontoffice/v2/tocs/${id}/sign`) as AxiosPromise
    },
    unsignToc(id: string) {
      return axiosInstance.post(`frontoffice/v2/tocs/${id}/unsign`) as AxiosPromise
    },
  },
}
